import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";

export interface AvailabilitySlot {
  start: string;
  end: string;
}

export interface AvailabilityResponse {
  slots: AvailabilitySlot[];
  timezone: string;
  disabled?: boolean;
}

interface UseAvailabilityArgs {
  durationMinutes: number;
  from: Date;
  to: Date;
  enabled?: boolean;
  location?: "online" | "office";
}

export function useAvailability({ durationMinutes, from, to, enabled = true, location = "online" }: UseAvailabilityArgs) {
  const { user } = useAuth();
  return useQuery<AvailabilityResponse>({
    queryKey: ["availability", durationMinutes, location, from.toISOString(), to.toISOString()],
    enabled: enabled && !!user,
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("get-availability", {
        body: {
          duration_minutes: durationMinutes,
          from: from.toISOString(),
          to: to.toISOString(),
          location,
        },
      });
      if (error) throw error;
      return data as AvailabilityResponse;
    },
  });
}

export function useBookingSettings() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["coach-booking-settings"],
    enabled: !!user,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_public_booking_settings");
      if (error) throw error;
      return (data && data[0]) || null;
    },
  });
}